/**
 * <s-slideshow> — Homepage hero slideshow. Slides are stacked server-rendered panels; this element
 * cross-fades between them, auto-advancing on an interval.
 *
 * Auto-advance begins only after the visitor first scrolls, moves the pointer, touches, or presses a
 * key. Until then slide 1 stays put, so a slide change never becomes the page's Largest Contentful
 * Paint or delays visual completeness for visitors who have not engaged yet.
 *
 * Accessibility: auto-advance pauses on hover, keyboard focus, hidden tabs, and when the visitor
 * presses the pause button; it never starts when the visitor prefers reduced motion. Inactive
 * slides are `inert` so their links can't be tabbed to.
 *
 * Attributes (on <s-slideshow>):
 *   data-interval     — Milliseconds between slides (default 6000).
 *   data-pause-label  — i18n label for the pause button.
 *   data-play-label   — i18n label for the play button.
 *
 * Required child markup:
 *   [data-slide]        — Each slide panel, in order. JS sets data-active="true|false" and inert.
 *   [data-slide-dot]    — One button per slide with data-index; JS sets aria-current.
 *   [data-slide-toggle] — Optional pause/play button.
 *   template[data-slide-media] — Optional, inside a slide: its background photo, inserted after the
 *     page load event (or just before that slide shows) so off-screen slides do not compete with
 *     slide 1 for first-load bandwidth.
 */
class Slideshow extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-slide-dot]'));
    this.toggle = this.querySelector('[data-slide-toggle]');
    if (this.slides.length < 2) return;

    this.index = 0;
    this.interval = Number(this.dataset.interval) || 6000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.userPaused = this.reducedMotion;
    this.hoverPaused = false;
    this.engaged = false;
    this.engageEvents = ['scroll', 'pointermove', 'pointerdown', 'touchstart', 'keydown'];
    this.onEngage = () => {
      this.engaged = true;
      this.removeEngageListeners();
      this.restart();
    };
    this.engageEvents.forEach((type) => window.addEventListener(type, this.onEngage, { passive: true, once: true }));

    this.onDotClick = (event) => {
      const dot = event.currentTarget;
      this.show(Number(dot.dataset.index));
      this.restart();
    };
    this.onToggle = () => {
      this.userPaused = !this.userPaused;
      this.updateToggle();
      this.restart();
    };
    this.onEnter = () => { this.hoverPaused = true; this.stop(); };
    this.onLeave = () => { this.hoverPaused = false; this.restart(); };
    this.onFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) this.onLeave();
    };
    this.onVisibility = () => (document.hidden ? this.stop() : this.restart());

    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
    this.toggle?.addEventListener('click', this.onToggle);
    this.addEventListener('pointerenter', this.onEnter);
    this.addEventListener('pointerleave', this.onLeave);
    this.addEventListener('focusin', this.onEnter);
    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('visibilitychange', this.onVisibility);

    this.onPageLoad = () => {
      const idle = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 200));
      idle(() => this.slides.forEach((slide) => this.loadMedia(slide)));
    };
    if (document.readyState === 'complete') this.onPageLoad();
    else window.addEventListener('load', this.onPageLoad, { once: true });

    this.show(0);
    this.updateToggle();
    this.restart();
  }

  disconnectedCallback() {
    this.stop();
    this.dots?.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
    this.toggle?.removeEventListener('click', this.onToggle);
    this.removeEventListener('pointerenter', this.onEnter);
    this.removeEventListener('pointerleave', this.onLeave);
    this.removeEventListener('focusin', this.onEnter);
    this.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.onPageLoad) window.removeEventListener('load', this.onPageLoad);
    this.removeEngageListeners();
  }

  removeEngageListeners() {
    this.engageEvents?.forEach((type) => window.removeEventListener(type, this.onEngage));
  }

  loadMedia(slide) {
    const template = slide?.querySelector('template[data-slide-media]');
    if (!template) return;
    slide.prepend(template.content.cloneNode(true));
    template.remove();
  }

  show(index) {
    const count = this.slides.length;
    this.index = ((index % count) + count) % count;
    this.loadMedia(this.slides[this.index]);
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.dataset.active = String(active);
      slide.inert = !active;
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.dots.forEach((dot, i) => {
      if (i === this.index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  updateToggle() {
    if (!this.toggle) return;
    this.toggle.setAttribute('aria-pressed', String(this.userPaused));
    this.toggle.setAttribute('aria-label', this.userPaused ? this.dataset.playLabel : this.dataset.pauseLabel);
    this.toggle.dataset.paused = String(this.userPaused);
  }

  stop() {
    window.clearInterval(this.timer);
    this.timer = undefined;
  }

  restart() {
    this.stop();
    if (!this.engaged || this.userPaused || this.hoverPaused || document.hidden) return;
    this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
  }
}

if (!customElements.get('s-slideshow')) {
  customElements.define('s-slideshow', Slideshow);
}
